import { useState, useEffect } from "react";
import { useAuth } from "../../App";
import AdminLayout from "../../components/admin/AdminLayout";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  Users,
  UserPlus,
  Search,
  Trash2,
  ShieldCheck,
  User,
  Mail,
  Key,
  Loader2,
  Eye,
  EyeOff,
  Building2,
  Edit,
  MoreHorizontal,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";

const ROLE_CONFIG = {
  all: { label: "All", color: "" },
  super_admin: { label: "Super Admin", bg: "bg-violet-50 text-violet-700 border-violet-200" },
  admin: { label: "Admin", bg: "bg-rnb-navy/10 text-rnb-navy border-rnb-navy/20" },
  consultant: { label: "Consultant", bg: "bg-rnb-blue/10 text-rnb-blue border-rnb-blue/20" },
  client: { label: "Client", bg: "bg-gray-100 text-gray-600 border-gray-200" },
  business_dev: { label: "Business Dev Partner", bg: "bg-amber-50 text-amber-700 border-amber-200" },
  sdr: { label: "Sales Dev Rep", bg: "bg-sky-50 text-sky-700 border-sky-200" },
  referral_partner: { label: "Partner", bg: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const AdminUsers = () => {
  const { user, token, API } = useAuth();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [createClientOpen, setCreateClientOpen] = useState(false);
  const [createAdminOpen, setCreateAdminOpen] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [selectedUserForReset, setSelectedUserForReset] = useState(null);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  const [editUserForm, setEditUserForm] = useState({
    name: "",
    email: "",
    company_id: "",
  });

  const [newClient, setNewClient] = useState({
    name: "",
    email: "",
    password: "",
    company_id: "",
    monthly_hours: "40",
    hourly_rate: "150",
  });

  const [newAdmin, setNewAdmin] = useState({
    name: "",
    email: "",
    password: "",
    role: "consultant",
  });

  useEffect(() => {
    fetchUsers();
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const res = await fetch(`${API}/companies/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setCompanies(await res.json());
    } catch (error) {
      console.error("Request failed:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setUsers(await res.json());
    } catch (error) {
      console.error("Request failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async () => {
    try {
      const params = new URLSearchParams({
        email: newClient.email,
        name: newClient.name,
        ...(newClient.password && { password: newClient.password }),
        ...(newClient.company_id && { company_id: newClient.company_id }),
        monthly_hours: newClient.monthly_hours,
        hourly_rate: newClient.hourly_rate,
      });
      const res = await fetch(`${API}/admin/users/client?${params.toString()}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        let errorMsg = "Failed to create client";
        try {
          const data = await res.json();
          errorMsg = typeof data.detail === "string" ? data.detail : Array.isArray(data.detail) ? data.detail.map(e => e.msg || e).join(", ") : "Failed to create client";
        } catch {}
        throw new Error(errorMsg);
      }
      toast.success("Client created successfully");
      setCreateClientOpen(false);
      setNewClient({ name: "", email: "", password: "", company_id: "", monthly_hours: "40", hourly_rate: "150" });
      fetchUsers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCreateAdmin = async () => {
    try {
      if (!newAdmin.password) {
        toast.error("Password is required for admin accounts");
        return;
      }
      const params = new URLSearchParams({
        email: newAdmin.email,
        name: newAdmin.name,
        password: newAdmin.password,
        role: newAdmin.role,
      });
      const res = await fetch(`${API}/admin/users/admin?${params.toString()}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        let errorMsg = "Failed to create user";
        try {
          const data = await res.json();
          errorMsg = typeof data.detail === "string" ? data.detail : Array.isArray(data.detail) ? data.detail.map(e => e.msg || e).join(", ") : "Failed to create user";
        } catch {}
        throw new Error(errorMsg);
      }
      toast.success(`${newAdmin.role === "admin" ? "Admin" : newAdmin.role === "business_dev" ? "Business Dev Partner" : newAdmin.role === "sdr" ? "SDR" : "Consultant"} created successfully`);
      setCreateAdminOpen(false);
      setNewAdmin({ name: "", email: "", password: "", role: "consultant" });
      fetchUsers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm("Are you sure you want to delete this user?")) return;
    try {
      const res = await fetch(`${API}/admin/users/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to delete user");
      }
      toast.success("User deleted");
      fetchUsers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleResendCredentials = async (userId, email) => {
    try {
      const res = await fetch(`${API}/admin/users/${userId}/resend-credentials`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to resend credentials");
      }
      toast.success(`Credentials email sent to ${email}`);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openResetPasswordDialog = (u) => {
    setSelectedUserForReset(u);
    setNewPassword("");
    setResetPasswordOpen(true);
  };

  const handleResetPassword = async () => {
    if (!selectedUserForReset) return;
    if (!newPassword || newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setResettingPassword(true);
    try {
      const params = new URLSearchParams({ new_password: newPassword });
      const res = await fetch(
        `${API}/admin/users/${selectedUserForReset.user_id}/reset-password?${params.toString()}`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        let errorMsg = "Failed to reset password";
        try {
          const data = await res.json();
          errorMsg = typeof data.detail === "string" ? data.detail : "Failed to reset password";
        } catch {}
        throw new Error(errorMsg);
      }
      toast.success(`Password reset for ${selectedUserForReset.email}. Credentials email sent.`);
      setResetPasswordOpen(false);
      setSelectedUserForReset(null);
      setNewPassword("");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setResettingPassword(false);
    }
  };

  const openEditUserDialog = (u) => {
    setSelectedUserForEdit(u);
    setEditUserForm({ name: u.name || "", email: u.email || "", company_id: u.company_id || "" });
    setEditUserOpen(true);
  };

  const handleSaveUserEdit = async () => {
    if (!selectedUserForEdit) return;
    if (!editUserForm.name.trim() || !editUserForm.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSavingEdit(true);
    try {
      const params = new URLSearchParams({ name: editUserForm.name, email: editUserForm.email });
      if (editUserForm.company_id) params.append("company_id", editUserForm.company_id);
      const res = await fetch(
        `${API}/admin/users/${selectedUserForEdit.user_id}?${params.toString()}`,
        { method: "PUT", headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to update user");
      }
      toast.success(`User ${editUserForm.name} updated successfully`);
      setEditUserOpen(false);
      setSelectedUserForEdit(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingEdit(false);
    }
  };

  const filteredUsers = users.filter((u) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
    const matchesRole = roleFilter === "all" || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const roleCounts = users.reduce((acc, u) => {
    acc[u.role] = (acc[u.role] || 0) + 1;
    return acc;
  }, {});

  const roleFilters = [
    { key: "all", label: "All", count: users.length },
    ...Object.entries(roleCounts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([role, count]) => ({
        key: role,
        label: ROLE_CONFIG[role]?.label || role,
        count,
      })),
  ];

  return (
    <AdminLayout activeNav="users">
      <div className="max-w-[1200px] mx-auto animate-fade-in">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1
              className="font-heading text-2xl font-bold text-rnb-navy tracking-tight"
              data-testid="users-page-title"
            >
              Users
            </h1>
            <p className="text-sm text-gray-400 mt-1">{users.length} total accounts</p>
          </div>
          <div className="flex gap-2">
            <Dialog open={createClientOpen} onOpenChange={setCreateClientOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs gap-1.5 h-9 border-gray-200 text-gray-500 hover:text-rnb-navy"
                  data-testid="create-client-btn"
                >
                  <User className="h-3.5 w-3.5" />
                  New Client
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Client</DialogTitle>
                  <DialogDescription>
                    Create a new client account with optional portal access.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Input value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input type="email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Password (optional)</Label>
                    <Input type="password" value={newClient.password} onChange={(e) => setNewClient({ ...newClient, password: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      Company
                    </Label>
                    <Select value={newClient.company_id || "none"} onValueChange={(v) => setNewClient({ ...newClient, company_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="client-company-select"><SelectValue placeholder="Select a company..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No company</SelectItem>
                        {companies.map((c) => <SelectItem key={c.company_id} value={c.company_id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Monthly Hours</Label>
                      <Input type="number" value={newClient.monthly_hours} onChange={(e) => setNewClient({ ...newClient, monthly_hours: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Hourly Rate ($)</Label>
                      <Input type="number" step="0.01" min="0" value={newClient.hourly_rate} onChange={(e) => setNewClient({ ...newClient, hourly_rate: e.target.value })} placeholder="150.00" />
                    </div>
                  </div>
                  <Button onClick={handleCreateClient} className="w-full bg-rnb-navy hover:bg-rnb-navy/90">Create Client</Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={createAdminOpen} onOpenChange={setCreateAdminOpen}>
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  className="text-xs gap-1.5 h-9 bg-rnb-navy hover:bg-rnb-navy/90"
                  data-testid="create-admin-btn"
                >
                  <ShieldCheck className="h-3.5 w-3.5" />
                  New Staff
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Admin/Consultant</DialogTitle>
                  <DialogDescription>Create a new admin or consultant account.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Name *</Label>
                      <Input value={newAdmin.name} onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input type="email" value={newAdmin.email} onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Password *</Label>
                    <Input type="password" value={newAdmin.password} onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Role *</Label>
                    <Select value={newAdmin.role} onValueChange={(v) => setNewAdmin({ ...newAdmin, role: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="consultant">Consultant</SelectItem>
                        <SelectItem value="admin">Super Admin</SelectItem>
                        <SelectItem value="business_dev">Business Dev Partner</SelectItem>
                        <SelectItem value="sdr">Sales Dev Rep</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleCreateAdmin} className="w-full bg-rnb-navy hover:bg-rnb-navy/90">
                    Create {newAdmin.role === "admin" ? "Admin" : newAdmin.role === "business_dev" ? "BDP" : newAdmin.role === "sdr" ? "SDR" : "Consultant"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Search + Role Filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl text-rnb-navy placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-rnb-blue/20 focus:border-rnb-blue/30 transition-all"
              data-testid="user-search"
            />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {roleFilters.map((rf) => (
              <button
                key={rf.key}
                onClick={() => setRoleFilter(rf.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 border ${
                  roleFilter === rf.key
                    ? "bg-rnb-navy text-white border-rnb-navy"
                    : "bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:text-rnb-navy"
                }`}
                data-testid={`role-filter-${rf.key}`}
              >
                {rf.label}
                <span className={`ml-1.5 ${roleFilter === rf.key ? "text-white/70" : "text-gray-400"}`}>
                  {rf.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-rnb-blue/20 border-t-rnb-blue rounded-full animate-spin" />
          </div>
        ) : (
          <div data-testid="users-list">
            {/* Column Header */}
            <div className="grid grid-cols-12 gap-4 px-5 py-2.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">
              <div className="col-span-4">User</div>
              <div className="col-span-3">Email</div>
              <div className="col-span-2">Role</div>
              <div className="col-span-3 text-right">Actions</div>
            </div>

            {filteredUsers.length > 0 ? (
              <div className="space-y-2">
                {filteredUsers.map((u) => {
                const initials = u.name?.split(" ").map((n) => n[0]).join("").slice(0, 2) || "?";
                const rc = ROLE_CONFIG[u.role] || ROLE_CONFIG.client;

                return (
                  <div
                    key={u.user_id}
                    className="grid grid-cols-12 gap-4 px-5 py-3.5 items-center bg-white rounded-xl hover:shadow-md transition-all duration-200 group"
                    data-testid={`user-row-${u.user_id}`}
                  >
                    {/* User */}
                    <div className="col-span-4 flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-rnb-navy/90 to-rnb-blue flex items-center justify-center text-white text-xs font-semibold shrink-0">
                        {u.picture ? (
                          <img src={u.picture} alt={u.name} className="w-9 h-9 rounded-lg object-cover" />
                        ) : (
                          initials
                        )}
                      </div>
                      <p className="text-sm font-medium text-rnb-navy truncate">{u.name}</p>
                    </div>

                    {/* Email */}
                    <div className="col-span-3 min-w-0">
                      <p className="text-sm text-gray-500 truncate">{u.email}</p>
                    </div>

                    {/* Role */}
                    <div className="col-span-2">
                      <Badge className={`text-[10px] px-2 py-0.5 border ${rc.bg}`}>
                        {rc.label}
                      </Badge>
                    </div>

                    {/* Actions */}
                    <div className="col-span-3 flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-gray-400 hover:text-rnb-navy"
                        title="Edit user"
                        onClick={() => openEditUserDialog(u)}
                        data-testid={`edit-user-${u.user_id}`}
                      >
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-gray-400 hover:text-rnb-navy"
                            data-testid={`user-actions-${u.user_id}`}
                          >
                            <MoreHorizontal className="h-3.5 w-3.5" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-44 p-1.5" align="end">
                          <button
                            onClick={() => openResetPasswordDialog(u)}
                            className="flex items-center gap-2 w-full px-3 py-2 text-xs text-gray-600 hover:bg-gray-50 rounded-md transition-colors"
                            data-testid={`reset-password-${u.user_id}`}
                          >
                            <Key className="h-3.5 w-3.5" /> Reset Password
                          </button>
                          <button
                            onClick={() => handleResendCredentials(u.user_id, u.email)}
                            className="flex items-center gap-2 w-full px-3 py-2 text-xs text-gray-600 hover:bg-gray-50 rounded-md transition-colors"
                            data-testid={`resend-credentials-${u.user_id}`}
                          >
                            <Mail className="h-3.5 w-3.5" /> Resend Credentials
                          </button>
                          {u.user_id !== user?.user_id && (
                            <button
                              onClick={() => handleDeleteUser(u.user_id)}
                              className="flex items-center gap-2 w-full px-3 py-2 text-xs text-red-600 hover:bg-red-50 rounded-md transition-colors"
                              data-testid={`delete-user-${u.user_id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" /> Delete User
                            </button>
                          )}
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                );
              })}
              </div>
            ) : (
              <div className="bg-white rounded-xl py-16 text-center">
                <Users className="h-10 w-10 text-gray-200 mx-auto mb-3" />
                <p className="text-sm text-gray-400">
                  {searchQuery || roleFilter !== "all" ? "No users match your filters" : "No users yet"}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Reset Password Dialog */}
        <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset Password</DialogTitle>
              <DialogDescription>
                Set a new password for {selectedUserForReset?.name} ({selectedUserForReset?.email}).
                They will receive an email with the new credentials.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>New Password</Label>
                <div className="relative">
                  <Input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    data-testid="admin-new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-400">Must be at least 8 characters</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResetPasswordOpen(false)}>Cancel</Button>
              <Button
                onClick={handleResetPassword}
                disabled={resettingPassword || !newPassword || newPassword.length < 8}
                className="bg-rnb-navy hover:bg-rnb-navy/90"
                data-testid="admin-reset-password-btn"
              >
                {resettingPassword ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Resetting...</>
                ) : (
                  <><Key className="h-4 w-4 mr-2" />Reset Password</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={editUserOpen} onOpenChange={setEditUserOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>
                Update information for {selectedUserForEdit?.name || selectedUserForEdit?.email}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name</Label>
                <Input id="edit-name" value={editUserForm.name} onChange={(e) => setEditUserForm({ ...editUserForm, name: e.target.value })} placeholder="Full name" data-testid="edit-user-name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input id="edit-email" type="email" value={editUserForm.email} onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })} placeholder="Email address" data-testid="edit-user-email" />
              </div>
              {selectedUserForEdit?.role === "client" && (
                <div className="space-y-2">
                  <Label htmlFor="edit-company">Company</Label>
                  <Select value={editUserForm.company_id || "none"} onValueChange={(v) => setEditUserForm({ ...editUserForm, company_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="edit-user-company"><SelectValue placeholder="Select company..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Company</SelectItem>
                      {companies.map((c) => <SelectItem key={c.company_id} value={c.company_id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditUserOpen(false)}>Cancel</Button>
              <Button
                onClick={handleSaveUserEdit}
                disabled={savingEdit || !editUserForm.name.trim() || !editUserForm.email.trim()}
                className="bg-rnb-navy hover:bg-rnb-navy/90"
                data-testid="save-user-edit-btn"
              >
                {savingEdit ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : "Save Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;
